import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Appointment, Job, User } from '@worksy360/shared';
import { api, ApiRequestError } from '../lib/api.js';
import { Badge, Button, Card, Dialog, EmptyState, Field, Input, Skeleton } from '../ui/index.js';
import { useToast } from '../ui/toast.js';
import { label, statusTone } from '../lib/format.js';

interface Board { date: string; technicians: { user: User; appointments: Appointment[] }[]; unassigned: Job[] }
interface Suggestion { userId: string; name: string; score: number; reasons: string[]; available: boolean }

function todayISO(): string { return new Date().toISOString().slice(0, 10); }
function addDaysISO(date: string, d: number): string {
  const dt = new Date(`${date}T00:00:00.000Z`); dt.setUTCDate(dt.getUTCDate() + d); return dt.toISOString().slice(0, 10);
}
function hm(iso: string): string { return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); }

export function Dispatch() {
  const [date, setDate] = useState(todayISO());
  const { data, isLoading } = useQuery({ queryKey: ['dispatch', date], queryFn: () => api.get<Board>(`/dispatch?date=${date}`) });
  const [assignJob, setAssignJob] = useState<Job | null>(null);

  return (
    <>
      <div className="page-head">
        <div><div className="page-title">Dispatch</div><div className="page-sub">Assign and track today's work</div></div>
        <div className="row gap-2">
          <Button variant="secondary" size="sm" onClick={() => setDate(addDaysISO(date, -1))}>‹</Button>
          <Button variant="secondary" size="sm" onClick={() => setDate(todayISO())}>Today</Button>
          <Button variant="secondary" size="sm" onClick={() => setDate(addDaysISO(date, 1))}>›</Button>
          <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ width: 160 }} />
        </div>
      </div>

      <div className="disp">
        <Card>
          <div className="card-head"><span className="card-title">Unassigned <span className="subtle">· {data?.unassigned.length ?? 0}</span></span></div>
          <div style={{ padding: '0 var(--sp-5)' }}>
            {isLoading ? <div className="stack gap-2" style={{ padding: 'var(--sp-4) 0' }}>{[0, 1].map((i) => <Skeleton key={i} h={40} />)}</div>
              : (data?.unassigned.length ?? 0) === 0 ? <div style={{ padding: 'var(--sp-6) 0' }}><span className="muted" style={{ fontSize: 'var(--text-sm)' }}>Nothing waiting to assign.</span></div>
              : data!.unassigned.map((j) => (
                <div key={j.id} className="queue-item">
                  <div className="stack"><strong style={{ fontSize: 'var(--text-sm)' }}>{j.title}</strong><span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>{j.number}{j.type ? ` · ${j.type}` : ''}</span></div>
                  <div className="row gap-2"><Badge tone={statusTone(j.priority)}>{label(j.priority)}</Badge><Button size="sm" onClick={() => setAssignJob(j)}>Assign</Button></div>
                </div>
              ))}
          </div>
        </Card>

        <Card>
          <div className="card-head"><span className="card-title">Technicians</span></div>
          {isLoading ? <div className="card-pad"><Skeleton h={120} /></div>
            : (data?.technicians.length ?? 0) === 0 ? <EmptyState icon="⇄" title="No technicians yet">Add team members with the Technician role to dispatch work.</EmptyState>
            : data!.technicians.map(({ user, appointments }) => (
              <div key={user.id} className="lane">
                <div className="lane-tech">
                  <span className="avatar">{user.name.split(' ').map((s) => s[0]).slice(0, 2).join('')}</span>
                  <div className="stack"><strong style={{ fontSize: 'var(--text-sm)' }}>{user.name}</strong><span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>{appointments.length} job{appointments.length === 1 ? '' : 's'}</span></div>
                </div>
                <div className="lane-appts">
                  {appointments.length === 0 ? <span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>Open</span>
                    : appointments.map((a) => <span key={a.id} className={`chip ${statusTone(a.status) === 'success' ? 'chip-success' : ''}`} title={a.title}>{hm(a.start)} · {a.title}</span>)}
                </div>
              </div>
            ))}
        </Card>
      </div>

      {assignJob && <AssignDialog job={assignJob} date={date} onClose={() => setAssignJob(null)} />}
    </>
  );
}

function AssignDialog({ job, date, onClose }: { job: Job; date: string; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [time, setTime] = useState('09:00');
  const [durationH, setDurationH] = useState('2');
  const [suggestions, setSuggestions] = useState<Suggestion[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState<string | null>(null);

  const window = () => {
    const start = new Date(`${date}T${time}:00`);
    const end = new Date(start.getTime() + (parseFloat(durationH || '1') || 1) * 3.6e6);
    return { start: start.toISOString(), end: end.toISOString() };
  };

  const loadSuggestions = async () => {
    setLoading(true);
    try {
      const { start, end } = window();
      const res = await api.get<{ suggestions: Suggestion[] }>(`/dispatch/suggest?jobId=${job.id}&customerId=${job.customerId}&start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`);
      setSuggestions(res.suggestions);
    } catch (err) {
      toast.push(err instanceof ApiRequestError ? err.message : 'Could not load suggestions', 'error');
    } finally { setLoading(false); }
  };

  const assign = async (userId: string) => {
    setAssigning(userId);
    try {
      const { start, end } = window();
      const res = await api.post<{ conflicts: unknown[] }>(`/customers/${job.customerId}/jobs/${job.id}/appointments`, { start, end, assignedUserIds: [userId] });
      qc.invalidateQueries({ queryKey: ['dispatch'] });
      qc.invalidateQueries({ queryKey: ['schedule'] });
      qc.invalidateQueries({ queryKey: ['jobs', 'needs_scheduling'] });
      toast.push(res.conflicts?.length ? 'Assigned (with a scheduling conflict)' : 'Job assigned', 'success');
      onClose();
    } catch (err) {
      toast.push(err instanceof ApiRequestError ? err.message : 'Assign failed', 'error');
    } finally { setAssigning(null); }
  };

  const maxScore = Math.max(1, ...(suggestions ?? []).map((s) => s.score));

  return (
    <Dialog title={`Assign · ${job.title}`} onClose={onClose}
      footer={<Button variant="secondary" onClick={onClose}>Close</Button>}>
      <div className="stack gap-4">
        <div className="row gap-3">
          <Field label="Date"><Input type="date" value={date} readOnly style={{ width: 150 }} /></Field>
          <Field label="Start"><Input type="time" value={time} onChange={(e) => { setTime(e.target.value); setSuggestions(null); }} /></Field>
          <Field label="Hours"><Input value={durationH} onChange={(e) => { setDurationH(e.target.value); setSuggestions(null); }} style={{ width: 70 }} inputMode="decimal" /></Field>
        </div>
        {!suggestions ? (
          <Button loading={loading} onClick={loadSuggestions}>Suggest best technician →</Button>
        ) : suggestions.length === 0 ? (
          <EmptyState icon="⇄" title="No assignable technicians">Add team members with the Technician role.</EmptyState>
        ) : (
          <div className="stack">
            <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>Ranked by fit — availability, skill match, and workload:</span>
            {suggestions.map((s) => (
              <div key={s.userId} className="sug">
                <div className="stack" style={{ flex: 1 }}>
                  <div className="row gap-2"><strong style={{ fontSize: 'var(--text-sm)' }}>{s.name}</strong>{s.available ? <Badge tone="success">Available</Badge> : <Badge tone="danger">Conflict</Badge>}</div>
                  <span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>{s.reasons.join(' · ')}</span>
                  <div className="sug-bar" style={{ width: `${Math.max(8, (s.score / maxScore) * 100)}%`, marginTop: 4 }} />
                </div>
                <Button size="sm" loading={assigning === s.userId} onClick={() => assign(s.userId)}>Assign</Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
}
